<template>
  <div>
    <!-- Email Configuration Status Card -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-email-cog</v-icon>
        <span class="headline">Email Configuration</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="fetchConfig" :loading="loadingConfig">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row v-if="config">
          <v-col cols="12" md="6">
            <v-list dense>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon
                    :color="config.api_key_configured ? 'success' : 'error'"
                  >
                    {{
                      config.api_key_configured
                        ? "mdi-check-circle"
                        : "mdi-close-circle"
                    }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>API Key</v-list-item-title>
                  <v-list-item-subtitle>
                    {{
                      config.api_key_configured
                        ? "Configured"
                        : "Not configured"
                    }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon :color="config.testing_mode ? 'warning' : 'success'">
                    {{
                      config.testing_mode
                        ? "mdi-test-tube"
                        : "mdi-rocket-launch"
                    }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Mode</v-list-item-title>
                  <v-list-item-subtitle>
                    {{
                      config.testing_mode ? "Testing Mode" : "Production Mode"
                    }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="12" md="6">
            <v-list dense>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon>mdi-email-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>From Address</v-list-item-title>
                  <v-list-item-subtitle>{{
                    config.from_address
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon>mdi-link</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Frontend URL</v-list-item-title>
                  <v-list-item-subtitle>{{
                    config.frontend_url
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
        <v-alert
          v-if="config && config.testing_mode"
          type="info"
          dense
          class="mt-2"
        >
          <strong>Testing Mode Active:</strong> Emails will be sent from
          <code>onboarding@resend.dev</code> (Resend's test address).
        </v-alert>
        <v-progress-circular
          v-if="loadingConfig && !config"
          indeterminate
        ></v-progress-circular>
      </v-card-text>
    </v-card>

    <!-- Send Test Email Card -->
    <v-card>
      <v-card-title>
        <v-icon left>mdi-email-send</v-icon>
        <span class="headline">Send Test Email</span>
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="formValid">
          <v-select
            v-model="testEmail.template_type"
            :items="templateOptions"
            label="Email Template"
            :rules="[v => !!v || 'Template is required']"
            outlined
            dense
          ></v-select>
          <v-text-field
            v-model="testEmail.to_email"
            label="Recipient Email"
            :rules="emailRules"
            outlined
            dense
            prepend-icon="mdi-email"
          ></v-text-field>
          <v-text-field
            v-model="testEmail.user_name"
            label="Test User Name"
            :rules="[v => !!v || 'Name is required']"
            outlined
            dense
            prepend-icon="mdi-account"
            hint="This name will appear in the email template"
          ></v-text-field>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :loading="sending"
          :disabled="!formValid || !config || !config.api_key_configured"
          @click="sendTestEmail"
        >
          <v-icon left>mdi-send</v-icon>
          Send Test Email
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Result Alert -->
    <v-alert
      v-if="result"
      :type="result.success ? 'success' : 'error'"
      dismissible
      class="mt-4"
      @input="result = null"
    >
      <div v-if="result.success">
        <strong>Email sent successfully!</strong>
        <div class="mt-1">
          Template: {{ result.template_type }} | To: {{ result.to_email }} |
          Mode: {{ result.testing_mode ? "Testing" : "Production" }}
        </div>
      </div>
      <div v-else>
        <strong>Failed to send email</strong>
        <div class="mt-1">{{ result.message }}</div>
      </div>
    </v-alert>
  </div>
</template>

<script>
import { apiClient } from "@shared/services/api";

export default {
  name: "AdminEmail",
  data: function () {
    return {
      config: null,
      loadingConfig: false,
      sending: false,
      formValid: false,
      result: null,
      testEmail: {
        template_type: "verification",
        to_email: "",
        user_name: "Test User"
      },
      templateOptions: [
        { text: "Email Verification", value: "verification" },
        { text: "Password Reset", value: "password_reset" },
        { text: "Welcome Email", value: "welcome" }
      ],
      emailRules: [
        function (v) {
          return !!v || "Email is required";
        },
        function (v) {
          return /.+@.+\..+/.test(v) || "Email must be valid";
        }
      ]
    };
  },
  methods: {
    fetchConfig: function () {
      var self = this;
      self.loadingConfig = true;
      apiClient
        .get("/admin/email/config")
        .then(function (res) {
          self.config = res.data;
        })
        .catch(function (error) {
          console.error("Fetch email config error:", error);
          self.$emit("error", "Failed to fetch email configuration");
        })
        .finally(function () {
          self.loadingConfig = false;
        });
    },
    sendTestEmail: function () {
      var self = this;
      if (!self.$refs.form.validate()) return;

      self.sending = true;
      self.result = null;

      apiClient
        .post("/admin/email/test", {
          template_type: self.testEmail.template_type,
          to_email: self.testEmail.to_email,
          user_name: self.testEmail.user_name
        })
        .then(function (res) {
          self.result = {
            success: true,
            template_type: res.data.template_type,
            to_email: res.data.to_email,
            testing_mode: res.data.testing_mode
          };
          self.$emit("success", "Test email sent successfully");
        })
        .catch(function (error) {
          console.error("Send test email error:", error);
          var message = "Failed to send test email";
          if (
            error.response &&
            error.response.data &&
            error.response.data.detail
          ) {
            message = error.response.data.detail;
          }
          self.result = {
            success: false,
            message: message
          };
          self.$emit("error", message);
        })
        .finally(function () {
          self.sending = false;
        });
    }
  },
  mounted: function () {
    this.fetchConfig();
  }
};
</script>
